import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// Private-pay invoices. Mirrors formatInvoice / formatLine in
// [server/src/routes/invoices.js]. A client-billed claim (bill_to='Client')
// lives as a Ready claim until a biller bundles it onto an invoice here;
// the invoice flips the claim to Submitted and locks its billing fields
// until the invoice is voided.

export type InvoiceStatus = 'Draft' | 'Issued' | 'Partial' | 'Paid' | 'Voided';
export type InvoiceDeliveryMethod = 'portal' | 'mail' | 'manual' | null;

export interface InvoiceLine {
    id: string;
    invoiceId: string;
    claimId: string;
    claimDisplayId: string;
    dos: string | null;
    serviceCode: string;
    units: number;
    unitRate: number;
    amountSnapshot: number;
    prSourceClaimId: string | null;
    prSourceDisplayId: string;
    prSourceDos: string | null;
}

export interface Invoice {
    id: string;
    displayId: string;
    clientId: string;
    clientName: string;
    status: InvoiceStatus;
    issuedAt: string | null;
    issuedBy: string | null;
    dueDate: string | null;
    subtotal: number;
    paidAmount: number;
    balance: number;
    payerRef: string;
    paymentDate: string | null;
    deliveryMethod: InvoiceDeliveryMethod;
    deliveredAt: string | null;
    notes: string;
    voidedAt: string | null;
    voidedBy: string | null;
    createdAt: string | null;
    /** Cumulative dollars refunded through Stripe — Phase 3's
     *  charge.refunded webhook rolls invoices.refunded_in_portal_cents
     *  forward; the staff Refund UI uses this to display a "Refunded"
     *  card. 0 when the invoice has had no portal refunds. */
    refundedInPortal: number;
    /** Dollars still refundable on the most recent succeeded portal
     *  payment (payment.amount − cumulative refunded). 0 when there's
     *  no portal payment to refund against; the Refund button is
     *  hidden in that case. The server is authoritative on the actual
     *  guard — this is a UX cap on the input. */
    portalPaymentRefundable: number;
    /** Phase 8 — per-invoice autopay opt-in (parent owns the toggle).
     *  Staff see it read-only on the invoice detail so they don't
     *  call the parent about an invoice that's about to charge
     *  itself. */
    autopayEnabled: boolean;
}

export interface InvoiceWithLines extends Invoice {
    lines: InvoiceLine[];
}

export interface InvoiceCreatePayload {
    clientId: string;
    claimIds: string[];
    dueDate?: string;   // YYYY-MM-DD
    notes?: string;
}

export interface InvoiceVoidPayload {
    reason?: string;
}

export interface InvoicePaymentPayload {
    paidAmount: number;
    paymentDate: string;   // YYYY-MM-DD
    payerRef?: string;
}

export interface InvoiceRefundPayload {
    /** Integer cents — server enforces a positive integer + the
     *  per-payment refund ceiling. */
    amountCents: number;
    /** Optional free-text rider (≤255 chars). Survives to Stripe via
     *  refund metadata + lands in the audit subject. */
    reason?: string;
}

export interface InvoiceRefundResponse {
    status: 'queued';
    /** The amount the server actually queued at Stripe — echoes the
     *  request after server-side validation. */
    amount_cents: number;
}

export interface InvoiceDeliveryPayload {
    method: Exclude<InvoiceDeliveryMethod, null>;
    deliveredAt?: string;  // ISO; server defaults to now
}

interface ListOpts {
    status?: string;     // CSV, e.g. 'Issued,Partial'
    clientId?: string;
    dateFrom?: string;
    dateTo?: string;
}

async function fetchInvoices(opts: ListOpts = {}): Promise<Invoice[]> {
    const params: Record<string, string> = {};
    if (opts.status)   params.status   = opts.status;
    if (opts.clientId) params.clientId = opts.clientId;
    if (opts.dateFrom) params.dateFrom = opts.dateFrom;
    if (opts.dateTo)   params.dateTo   = opts.dateTo;
    const res = await api.get<Invoice[]>('/invoices', { params });
    return res.data;
}

export function useInvoices(opts: ListOpts = {}) {
    return useQuery({
        queryKey: ['invoices',
            opts.status   ?? 'any',
            opts.clientId ?? 'any',
            opts.dateFrom ?? 'any',
            opts.dateTo   ?? 'any',
        ] as const,
        queryFn: () => fetchInvoices(opts),
        staleTime: 30_000,
    });
}

export function useInvoice(id: string | undefined) {
    return useQuery({
        queryKey: ['invoices', 'entry', id] as const,
        queryFn: async () => {
            const res = await api.get<InvoiceWithLines>(`/invoices/${id}`);
            return res.data;
        },
        enabled: !!id,
    });
}

export function useCreateInvoice() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: InvoiceCreatePayload) => {
            const res = await api.post<Invoice>('/invoices', payload);
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['invoices'] });
            qc.invalidateQueries({ queryKey: ['claims'] });
        },
    });
}

export function useVoidInvoice() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload?: InvoiceVoidPayload }) => {
            const res = await api.post<Invoice>(`/invoices/${id}/void`, payload ?? {});
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['invoices'] });
            qc.invalidateQueries({ queryKey: ['invoices', 'entry', vars.id] });
            qc.invalidateQueries({ queryKey: ['claims'] });
        },
    });
}

export function useRecordInvoicePayment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: InvoicePaymentPayload }) => {
            const res = await api.post<Invoice>(`/invoices/${id}/payment`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['invoices'] });
            qc.invalidateQueries({ queryKey: ['invoices', 'entry', vars.id] });
            qc.invalidateQueries({ queryKey: ['claims'] });
        },
    });
}

// Phase 6 of pma-stripe-payments — staff-side refund of a portal
// payment. The server returns 202 + { status: 'queued' }; the actual
// invoices.refunded_in_portal_cents flip waits for the Stripe
// charge.refunded webhook (usually 1-5s). Invalidating the invoice
// query here primes the next refetch to pick up the column once the
// webhook lands.
export function useRefundPortalPayment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: InvoiceRefundPayload }) => {
            const body: Record<string, unknown> = { amount_cents: payload.amountCents };
            if (payload.reason && payload.reason.trim()) body.reason = payload.reason.trim();
            const res = await api.post<InvoiceRefundResponse>(`/invoices/${id}/refunds`, body);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['invoices'] });
            qc.invalidateQueries({ queryKey: ['invoices', 'entry', vars.id] });
        },
    });
}

export function useMarkInvoiceDelivered() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: InvoiceDeliveryPayload }) => {
            const res = await api.post<Invoice>(`/invoices/${id}/delivery`, payload);
            return res.data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['invoices'] });
            qc.invalidateQueries({ queryKey: ['invoices', 'entry', vars.id] });
        },
    });
}

// Downloads the rendered .docx via the browser. Uses responseType:'blob' so
// axios doesn't parse the zipped payload as text. Content-Disposition carries
// the INV-###.docx filename; we fall back to the display id if a caller
// wants to name the file locally.
export function useDownloadInvoiceDocx() {
    return useMutation({
        mutationFn: async ({ id, displayId }: { id: string; displayId?: string }): Promise<{ filename: string }> => {
            const res = await api.get(`/invoices/${id}/docx`, { responseType: 'blob' });

            const cd = res.headers['content-disposition'] as string | undefined;
            const match = cd && /filename="([^"]+)"/.exec(cd);
            const filename = match ? match[1] : `${displayId || 'invoice'}.docx`;

            const objectUrl = URL.createObjectURL(res.data as Blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);

            return { filename };
        },
    });
}
